import {compact} from '@benchling/js-utils';
import {css} from 'aphrodite';
import React from 'react';

import {getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import {UpdateTaskParams} from 'processes/process-task-details/utils/updateTaskDetailsTask';
import {isFlowchartType} from 'processes/processes-platform/FeatureOptions';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {AvatarWithName} from 'ui-library/avatar/AvatarWithName';
import AnalysisChip from 'ui-library/chips/AnalysisChip';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcessChip from 'ui-library/chips/ProcessChip';
import ProcessTaskChip from 'ui-library/chips/ProcessTaskChip';
import * as DateUtil from 'util/DateUtil';

import FormattedTextField from '../fields/FormattedTextField';
import PlaceholderFieldValue from '../fields/PlaceholderFieldValue';
import SourceTasksAndOutputsField from '../fields/SourceTasksAndOutputsField';
import StatusField from '../fields/StatusField';
import {
  ProcessTaskDetailsRowContent,
  getProcessTaskDetailsLabels,
  ProcessTaskGql,
  PROCESS_TASK_DETAILS_PAGE_STYLES,
} from '../ProcessTaskDetailsPageConstants';

export function getPipelineTaskDetailsSummaryRows(
  processTask: ProcessTaskGql,
  onUpdateTask: (updatedTask: UpdateTaskParams) => Promise<void>
): Array<ProcessTaskDetailsRowContent> {
  return [
    {
      label: getProcessTaskDetailsLabels().STATUS,
      component: <StatusField processTask={processTask} onUpdateTask={onUpdateTask} />,
    },
    {
      label: getProcessTaskDetailsLabels().EXECUTED_IN,
      component:
        processTask.analysis != null ? (
          <AnalysisChip id={getAccessibleOrInaccessibleId(processTask.analysis)} size={ChipSize.TABLE_CELL} />
        ) : (
          <PlaceholderFieldValue />
        ),
    },
  ];
}

export function getPipelineTaskDetailsMetadataRows(
  processTask: ProcessTaskGql
): Array<ProcessTaskDetailsRowContent> {
  const processTaskDetailsLabels = getProcessTaskDetailsLabels();
  return compact([
    {
      label: processTaskDetailsLabels.ID,
      component: <FormattedTextField text={processTask.humanId} />,
    },
    {
      label: processTaskDetailsLabels.CREATOR,
      component: (
        <AvatarWithName
          avatarInfo={processTask.creator}
          size={AvatarSize.XXSMALL}
          shape={AvatarShape.CIRCLE}
          showTooltip={true}
        />
      ),
    },
    {
      label: processTaskDetailsLabels.CREATED,
      component: <FormattedTextField text={DateUtil.readableTime(processTask.createdAt)} />,
    },
    {
      label: processTaskDetailsLabels.MODIFIED,
      component: (
        <span className={css(PROCESS_TASK_DETAILS_PAGE_STYLES.rowHeight)}>
          {DateUtil.readableTime(processTask.modifiedAt)}
        </span>
      ),
    },
    {
      label: processTaskDetailsLabels.SOURCES,
      component: <SourceTasksAndOutputsField processTask={processTask} />,
    },
    {
      label: processTaskDetailsLabels.FLOWCHART_TASK,
      component: (
        <>
          {(() => {
            if (processTask.rootTask != null) {
              return (
                <ProcessTaskChip
                  className={css(PROCESS_TASK_DETAILS_PAGE_STYLES.chipHeight)}
                  id={processTask.rootTask.id}
                  size={ChipSize.TABLE_CELL}
                />
              );
            } else if (isFlowchartType(processTask.schema.executionType)) {
              return <FormattedTextField text="(This is the flowchart task)" />;
            } else {
              return <PlaceholderFieldValue />;
            }
          })()}
        </>
      ),
    },
    {
      label: processTaskDetailsLabels.TASK_GROUP,
      component: (
        <ProcessChip
          className={css(PROCESS_TASK_DETAILS_PAGE_STYLES.chipHeight)}
          id={processTask.process.id}
          size={ChipSize.TABLE_CELL}
        />
      ),
    },
  ]);
}
